/**
 * PendingAcknowledgementsWidget — Dashboard banner listing procedures
 * the current user has not yet acknowledged. Hidden when everything is
 * up to date.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BookOpen, ArrowRight, AlertCircle } from "lucide-react";

export default function PendingAcknowledgementsWidget() {
  const [rows, setRows] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/procedures/pending-acknowledgements");
        if (!cancelled) setRows(r.data || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <Card
      className="p-4 rounded-lg border border-amber-300 bg-amber-50 shadow-none"
      data-testid="dashboard-procedures-pending"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-amber-900">
          <AlertCircle className="h-4 w-4" />
          <div>
            <div className="text-sm font-semibold">
              {rows.length} procedure{rows.length === 1 ? "" : "s"} to acknowledge
            </div>
            <div className="text-2xs text-amber-800">
              New or updated SOPs need your sign-off.
            </div>
          </div>
        </div>
        <Link
          to="/learning?tab=procedures"
          className="text-xs text-amber-900 hover:text-amber-950 inline-flex items-center gap-1 font-medium"
          data-testid="dashboard-procedures-see-all"
        >
          Open procedures <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <ul className="space-y-1">
        {rows.slice(0, 5).map((p) => (
          <li key={p.procedure_id} className="flex items-center gap-2 text-sm" data-testid={`dashboard-procedure-pending-${p.procedure_id}`}>
            <BookOpen className="h-3.5 w-3.5 text-amber-700 shrink-0" />
            <Link
              to={`/procedures/${p.procedure_id}`}
              className="flex-1 truncate hover:underline text-amber-950"
            >
              {p.title}
              <span className="ml-1.5 text-2xs font-mono-num text-amber-700">v{p.version}</span>
            </Link>
            {p.category && (
              <span className="text-2xs uppercase tracking-wider text-amber-800">{p.category}</span>
            )}
          </li>
        ))}
        {rows.length > 5 && (
          <li className="text-2xs text-amber-800 pt-1">…and {rows.length - 5} more</li>
        )}
      </ul>
    </Card>
  );
}
